import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog, StatusBadge, toneForStatus } from '@ibe/ui';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { DetailField, DetailGrid } from '../../../components/resource-detail/detail-grid.js';
import { ResourceDetailPage } from '../../../components/resource-detail/resource-detail-page.js';
import { useTenant } from '../api/queries.js';
import { useSoftDeleteTenant } from '../api/mutations.js';

export function TenantDetailPage(): React.JSX.Element {
  const tenantId = useParams().tenantId ?? '';
  const navigate = useNavigate();
  const query = useTenant(tenantId);

  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const softDelete = useSoftDeleteTenant();

  const onDelete = async (): Promise<void> => {
    await softDelete.mutateAsync(tenantId);
    setConfirmingDelete(false);
    void navigate('/tenants');
  };

  return (
    <ResourceDetailPage
      query={query}
      permission={PERMISSIONS.TENANT_READ}
      parent={{ to: '/tenants', label: 'Tenants' }}
      title={(data) => data.name}
      actions={
        <PermissionGuard permission={PERMISSIONS.TENANT_UPDATE}>
          <Button asChild variant="outline">
            <Link to={`/tenants/${tenantId}/edit`}>Edit</Link>
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              setConfirmingDelete(true);
            }}
          >
            Delete
          </Button>
        </PermissionGuard>
      }
    >
      {(data) => (
        <>
          <ConfirmDialog
            open={confirmingDelete}
            onOpenChange={setConfirmingDelete}
            title="Delete tenant"
            description={`This will soft-delete ${data.name}. The row remains in the database but is no longer available for new bookings.`}
            confirmLabel="Delete"
            destructive
            loading={softDelete.isPending}
            onConfirm={() => {
              void onDelete();
            }}
          />

          <DetailGrid>
            <DetailField label="Slug">{data.slug}</DetailField>
            <DetailField label="Status">
              <StatusBadge tone={toneForStatus(data.status)}>{data.status}</StatusBadge>
            </DetailField>
            <DetailField label="Timezone">{data.timezone}</DetailField>
            <DetailField label="Contact">{data.contactName}</DetailField>
            <DetailField label="Email">{data.email}</DetailField>
            <DetailField label="Phone">{data.phone}</DetailField>
            <DetailField label="Address">{data.address}</DetailField>
            <DetailField label="Location">
              {[data.countryName, data.stateName, data.cityName]
                .filter((part): part is string => part !== null && part !== '')
                .join(', ')}
            </DetailField>
            <DetailField label="Modules">
              {/* "CORE only" rather than an empty cell: CORE is never stored,
                  so an empty array means "bought nothing", not "has nothing".
                  A blank here reads as missing data. */}
              {data.enabledModules.length === 0 ? 'CORE only' : data.enabledModules.join(', ')}
            </DetailField>
            <DetailField label="Sign-in address">
              {/* The Gateway resolves a tenant from the Host header, so this
                  is the only address these accounts exist at. Shown because
                  the setup email is best-effort: when it does not arrive, this
                  is where an operator reads the link off to pass on by hand. */}
              {data.loginUrl ?? 'No domain yet — its users cannot sign in anywhere'}
            </DetailField>
            <DetailField label="Version">{String(data.version)}</DetailField>
          </DetailGrid>

          <section className="space-y-2">
            <h2 className="text-sm font-semibold">Administrators</h2>
            {data.admins.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                {/* A real state, not a loading one: tenants created before the
                    flag existed, and any made through the old path that wrote
                    no owner, genuinely have none. */}
                None. Nobody can sign into this tenant until one is added.
              </p>
            ) : (
              <ul className="text-sm">
                {data.admins.map((admin) => (
                  <li key={admin.id} className="flex flex-wrap items-center gap-x-2">
                    <span className="font-medium">
                      {[admin.firstName, admin.lastName].filter(Boolean).join(' ') || admin.email}
                    </span>
                    <span className="text-muted-foreground">{admin.email}</span>
                    <span className="text-muted-foreground">· {admin.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </ResourceDetailPage>
  );
}
